import { motion } from "framer-motion";
import { Fuel, Building2, Factory, Cpu, Waves, ArrowUpRight } from "lucide-react";

const STRATEGIES = [
  {
    icon: Fuel,
    name: "Energy",
    theme: "Global Energy Continuum",
    copy: "From international crude and LNG/LPG supply chains to distributed clean generation, we deploy capital where operational depth and long-term offtake create durable value.",
    focus: ["International crude & product trading", "LNG / LPG midstream", "Refinery relationships & operations", "Clean generation & storage"],
  },
  {
    icon: Building2,
    name: "Infrastructure",
    theme: "Institutional Backbones",
    copy: "Ports, pipelines, transmission, and public-private frameworks that carry regional economies through the next generation of growth.",
    focus: ["Pipeline infrastructure", "Terminals & logistics", "PPP structures", "Transmission & grid"],
  },
  {
    icon: Factory,
    name: "Commodities",
    theme: "Cross-Border Flows",
    copy: "Petrochemicals, industrial feedstocks, and supply-chain finance — anchored by deep relationships with state-owned enterprises and global refiners.",
    focus: ["Petrochemical supply chains", "Commodity procurement", "Trade finance", "Hedging & structuring"],
  },
  {
    icon: Cpu,
    name: "Technology",
    theme: "AI, Compute & Real Assets",
    copy: "AI-ready hyperscale campuses, sovereign compute, enterprise transformation, and the power infrastructure required to sustain them.",
    focus: ["Data center campuses", "Sovereign compute", "AI enterprise transformation", "On-site power"],
  },
  {
    icon: Waves,
    name: "Real Assets",
    theme: "Long-Duration Value",
    copy: "ESG-aligned mixed-use, industrial, and specialty real estate — designed for resilient cash yield and community-scale outcomes.",
    focus: ["Mixed-use development", "Industrial & logistics", "Specialty real estate", "ESG-aligned outcomes"],
  },
];

export default function Strategies() {
  return (
    <div>
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-32 pb-16">
        <div className="eyebrow">Investment Strategies</div>
        <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[1.02] max-w-4xl">
          Five verticals. One disciplined thesis.
        </h1>
        <p className="mt-8 max-w-3xl text-body">
          Each strategy sits inside an integrated global platform — sharing diligence, sourcing,
          and risk oversight. Our leadership executes with local depth on the ground and
          institutional governance from the United States.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {STRATEGIES.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06, duration: 0.7 }}
              className="border border-[#0B1F3A]/10 bg-white p-10 hover:shadow-xl hover:-translate-y-1 transition group flex flex-col"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 border border-[#D4AF37] flex items-center justify-center">
                    <s.icon size={22} strokeWidth={1.4} className="text-[#0B1F3A]" />
                  </div>
                  <div>
                    <div className="eyebrow" style={{ color: "#3399FF" }}>{s.name}</div>
                    <div className="font-display text-2xl mt-1 text-[#0B1F3A]">{s.theme}</div>
                  </div>
                </div>
                <ArrowUpRight className="text-[#D4AF37] opacity-0 group-hover:opacity-100 transition" />
              </div>
              <p className="mt-8 text-body text-sm">{s.copy}</p>
              <div className="hairline my-8" />
              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-[#0B1F3A]">
                {s.focus.map(f => (
                  <li key={f} className="flex gap-2">
                    <span className="text-[#D4AF37]">—</span>{f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-[#0B1F3A] text-[#F8F7F2] py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="eyebrow" style={{ color: "#D4AF37" }}>Global Relationships</div>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
              Access built on long-term, institution-to-institution trust.
            </h2>
          </div>
          <div className="md:col-span-7 text-[#F8F7F2]/80 leading-relaxed">
            <p>
              Our operating model is built upon long-term strategic relationships with state-owned
              enterprises, refineries, commodity producers, financial institutions, governments,
              infrastructure developers, technology companies, institutional investors, family
              offices, and development organizations.
            </p>
            <p className="mt-4">
              These relationships provide First Providence Capital with the ability to execute
              transactions requiring deep local expertise, cross-border coordination, and
              institutional standards of governance.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
